import { useEffect, useState } from "react";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, FileText, ShoppingCart, ReceiptText, IndianRupee, Truck, Orbit } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  lead: { icon: Sparkles, ring: "border-amber-300 bg-amber-50 text-amber-600", label: "Lead", plural: "Leads" },
  quotation: { icon: FileText, ring: "border-blue-300 bg-blue-50 text-blue-600", label: "Quotation", plural: "Quotations" },
  order: { icon: ShoppingCart, ring: "border-indigo-300 bg-indigo-50 text-indigo-600", label: "Order", plural: "Orders" },
  invoice: { icon: ReceiptText, ring: "border-slate-300 bg-slate-50 text-slate-600", label: "Invoice", plural: "Invoices" },
  payment: { icon: IndianRupee, ring: "border-emerald-300 bg-emerald-50 text-emerald-600", label: "Payment", plural: "Payments" },
  dispatch: { icon: Truck, ring: "border-cyan-300 bg-cyan-50 text-cyan-600", label: "Dispatch", plural: "Dispatches" },
};

const STATUS_COLOR = {
  Overdue: "text-rose-600 bg-rose-50 border-rose-200",
  Paid: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Received: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Won: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Converted: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Unpaid: "text-amber-700 bg-amber-50 border-amber-200",
  Partial: "text-amber-700 bg-amber-50 border-amber-200",
};

function Stat({ label, value, sub, accent }) {
  return (
    <div className="border border-slate-200 bg-white rounded-lg p-3.5" data-testid={`c360-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className={`mt-1 font-display text-xl font-bold tabular-nums ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const FILTERS = ["all", "quotation", "order", "invoice", "payment", "dispatch", "lead"];

export default function Customer360({ customer, onClose }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    if (!customer) return;
    setData(null);
    setFilter("all");
    api.get(`/customer360/${customer.id}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); onClose(); });
    // eslint-disable-next-line
  }, [customer?.id]);

  if (!customer) return null;
  const s = data?.summary;
  const events = (data?.timeline || []).filter((e) => filter === "all" || e.type === filter);

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right" data-testid="c360-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Orbit size={18} className="text-[#FC5F5F]" />
            <span>Customer 360° — {customer.company_name}</span>
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="text-sm text-slate-500 py-16 text-center">Loading full history…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
              <Stat label="Total Business" value={inr(s.total_business)} sub={`${s.invoices} invoice(s)`} />
              <Stat label="Outstanding" value={inr(s.outstanding)} accent={s.outstanding > 0 ? "text-rose-600" : "text-emerald-700"} />
              <Stat label="Received" value={inr(s.total_received)} accent="text-emerald-700" />
              <Stat label="Orders" value={s.orders} sub={s.avg_order_value ? `avg ${inr(s.avg_order_value)}` : ""} />
              <Stat label="Quote → Order" value={s.conversion_pct != null ? `${s.conversion_pct}%` : "—"} sub={`${s.quotes} quotation(s)`} />
              <Stat label="Last Order" value={s.last_order ? fmtDate(s.last_order) : "—"} sub={s.first_activity ? `since ${fmtDate(s.first_activity)}` : ""} />
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="c360-filters">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`c360-filter-${f}`}
                        className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors ${filter === f ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
                  {f === "all" ? `All (${data.timeline.length})` : TYPE_META[f].plural}
                </button>
              ))}
            </div>

            <div className="relative pl-5" data-testid="c360-timeline">
              <div className="absolute left-[13px] top-2 bottom-2 w-px bg-slate-200" />
              <div className="space-y-2.5">
                {events.map((e, i) => {
                  const M = TYPE_META[e.type] || TYPE_META.invoice;
                  const Icon = M.icon;
                  return (
                    <div key={i} className="relative flex items-start gap-3">
                      <span className={`relative z-10 -ml-5 mt-1 w-7 h-7 rounded-full border flex items-center justify-center flex-none ${M.ring}`}>
                        <Icon size={13} />
                      </span>
                      <div className="flex-1 border border-slate-200 bg-white rounded-lg px-4 py-2.5 hover:shadow-sm transition-shadow">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-semibold text-slate-800">{e.title}</span>
                          {e.status && (
                            <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_COLOR[e.status] || "text-slate-500 bg-slate-50 border-slate-200"}`}>{e.status}</span>
                          )}
                          <span className="ml-auto flex items-center gap-3">
                            {e.amount != null && <span className="text-sm font-bold tabular-nums text-slate-900">{inr(e.amount)}</span>}
                            <span className="text-[11px] text-slate-400">{fmtDate(e.date)}</span>
                          </span>
                        </div>
                        {e.sub && <div className="text-xs text-slate-500 mt-0.5">{e.sub}</div>}
                      </div>
                    </div>
                  );
                })}
                {events.length === 0 && (
                  <div className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-lg">No history for this filter yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
